import AnalysisVisibility from "../analysis/AnalysisVisibility";
import Utils from "../basic/Utils";
import DitapPopup from "./DitapPopup";

/**
 * 슬라이더 range 색상 적용.</br>
 * Slider percentage color applied
 * @private
 * @param {Element} input
 */
function slider(input) {
  const min = Number(input.min);
  const max = Number(input.max);
  const value = Number(input.value);
  const percentage = ((value - min) / (max - min)) * 100;
  input.style.backgroundImage =
    "linear-gradient(to right, #EEC252 0%, #EEC252 " +
    `${percentage}` +
    "%, #AAA " +
    `${percentage}` +
    "%, #AAA 100%)";
}

/**
 * 가시성 분석 팝업 클래스.</br>
 * AnalysisVisibility popup class.
 * @alias PopupAnalysisVisibility
 * @extends DitapPopup
 *
 * @constructor
 * @param {AnalysisVisibility} analysisVisibility 가시성 분석 연산 및 영역 표시 클래스.</br> Visibility analysis operation and area display class.
 *
 * @example
 * const viewer = new Ditap.DitapViewer("ditapContainer");
 *
 * const destination = Ditap.Cartesian3.fromDegrees(
 *   126.978275264,
 *   37.566642192,
 *   400
 * );
 *
 * // 카메라 이동
 * viewer.scene.camera.flyTo({
 *   destination: destination,
 * });
 *
 * // 1. 분석이 진행된 AnalysisVisibility 생성
 * const position = new Ditap.Cartesian3(-3044811.6264139246, 4043796.6433953536, 3867466.40813661);
 * const props = {
 *   position: position
 * };
 * const analysisVisibility = new Ditap.AnalysisVisibility(viewer, props);
 *
 * // 2. 분석 객체 할당
 * const popupAnalysisVisibility = new Ditap.PopupAnalysisVisibility(analysisVisibility);
 * popupAnalysisVisibility.open();
 */
class PopupAnalysisVisibility extends DitapPopup {
  constructor(analysisVisibility) {
    const parent = analysisVisibility?.viewer?.container;
    super(parent, analysisVisibility.viewer.ditapPaneManager);

    this._analysisVisibility = undefined;
    if (analysisVisibility instanceof AnalysisVisibility) {
      if (analysisVisibility.isReady) {
        this._analysisVisibility = analysisVisibility;
      } else {
        throw Error("PopupAnalysisVisibility analysisVisibility is not ready");
      }
    } else {
      throw Error("PopupAnalysisVisibility analysisVisibility type error");
    }

    // 팝업 생성
    const infoBox = this.createInfoBox();
    // 헤더 추가
    this.prependHeader(infoBox["header"]["title"]);
    // 컨텐츠 추가
    this.appendSubTitle(infoBox["content"]["subTitle"]);
    this.appendContent(infoBox["content"]["visibilityControlList"]);
    this.appendFooter("");
    // 팝업 설정
    this.wrapper.classList.remove("ditap-size-popup");
    this.footer.remove();

    // 노드 세팅
    this._visibilityControlHeadingMinusP =
      infoBox["content"]["visibilityControlHeadingMinusP"];
    this._visibilityControlHeadingPlusP =
      infoBox["content"]["visibilityControlHeadingPlusP"];
    this._visibilityControlHeadingSliderInput =
      infoBox["content"]["visibilityControlHeadingSliderInput"];
    this._visibilityControlHeadingMinValue =
      infoBox["content"]["visibilityControlHeadingMinValue"];
    this._visibilityControlHeadingCurrentValue =
      infoBox["content"]["visibilityControlHeadingCurrentValue"];
    this._visibilityControlHeadingMaxValue =
      infoBox["content"]["visibilityControlHeadingMaxValue"];
    this._visibilityControlPitchMinusP =
      infoBox["content"]["visibilityControlPitchMinusP"];
    this._visibilityControlPitchPlusP =
      infoBox["content"]["visibilityControlPitchPlusP"];
    this._visibilityControlPitchSliderInput =
      infoBox["content"]["visibilityControlPitchSliderInput"];
    this._visibilityControlPitchMinValue =
      infoBox["content"]["visibilityControlPitchMinValue"];
    this._visibilityControlPitchCurrentValue =
      infoBox["content"]["visibilityControlPitchCurrentValue"];
    this._visibilityControlPitchMaxValue =
      infoBox["content"]["visibilityControlPitchMaxValue"];
    this._visibilityControlFovMinusP =
      infoBox["content"]["visibilityControlFovMinusP"];
    this._visibilityControlFovPlusP =
      infoBox["content"]["visibilityControlFovPlusP"];
    this._visibilityControlFovSliderInput =
      infoBox["content"]["visibilityControlFovSliderInput"];
    this._visibilityControlFovMinValue =
      infoBox["content"]["visibilityControlFovMinValue"];
    this._visibilityControlFovCurrentValue =
      infoBox["content"]["visibilityControlFovCurrentValue"];
    this._visibilityControlFovMaxValue =
      infoBox["content"]["visibilityControlFovMaxValue"];
    this._visibilityControlAspectRatioMinusP =
      infoBox["content"]["visibilityControlAspectRatioMinusP"];
    this._visibilityControlAspectRatioPlusP =
      infoBox["content"]["visibilityControlAspectRatioPlusP"];
    this._visibilityControlAspectRatioSliderInput =
      infoBox["content"]["visibilityControlAspectRatioSliderInput"];
    this._visibilityControlAspectRatioMinValue =
      infoBox["content"]["visibilityControlAspectRatioMinValue"];
    this._visibilityControlAspectRatioCurrentValue =
      infoBox["content"]["visibilityControlAspectRatioCurrentValue"];
    this._visibilityControlAspectRatioMaxValue =
      infoBox["content"]["visibilityControlAspectRatioMaxValue"];
    this._visibilityControlDistanceMinusP =
      infoBox["content"]["visibilityControlDistanceMinusP"];
    this._visibilityControlDistancePlusP =
      infoBox["content"]["visibilityControlDistancePlusP"];
    this._visibilityControlDistanceSliderInput =
      infoBox["content"]["visibilityControlDistanceSliderInput"];
    this._visibilityControlDistanceMinValue =
      infoBox["content"]["visibilityControlDistanceMinValue"];
    this._visibilityControlDistanceCurrentValue =
      infoBox["content"]["visibilityControlDistanceCurrentValue"];
    this._visibilityControlDistanceMaxValue =
      infoBox["content"]["visibilityControlDistanceMaxValue"];

    // 이벤트 설정
    this.setEvent();

    // 기본 값 설정
    this.update();

    this.changeCloseBtn();
    const isMobile = Utils.isBodyWidthLessThan600();
    if (!isMobile) {
      this.enableDrag();
      this.enableTouch();
    }
  }

  /**
   * 가시성 분석 연산 및 영역 표시 클래스
   * Visibility analysis operation and area display class.
   * @readonly
   * @type {AnalysisVisibility}
   */
  get analysisVisibility() {
    return this._analysisVisibility;
  }
  set analysisVisibility(analysisVisibility) {
    this._analysisVisibility = analysisVisibility;
  }

  /**
   * 헤딩 마이너스 버튼 요소.<br>
   * Heading minus button element.
   * @readonly
   * @type {Element}
   */
  get visibilityControlHeadingMinusP() {
    return this._visibilityControlHeadingMinusP;
  }
  set visibilityControlHeadingMinusP(visibilityControlHeadingMinusP) {
    this._visibilityControlHeadingMinusP = visibilityControlHeadingMinusP;
  }

  /**
   * 헤딩 플러스 버튼 요소.<br>
   * Heading plus button element.
   * @readonly
   * @type {Element}
   */
  get visibilityControlHeadingPlusP() {
    return this._visibilityControlHeadingPlusP;
  }
  set visibilityControlHeadingPlusP(_visibilityControlHeadingPlusP) {
    this._visibilityControlHeadingPlusP = _visibilityControlHeadingPlusP;
  }

  /**
   * 헤딩 슬라이더 요소.<br>
   * Heading slider element.
   * @readonly
   * @type {Element}
   */
  get visibilityControlHeadingSliderInput() {
    return this._visibilityControlHeadingSliderInput;
  }
  set visibilityControlHeadingSliderInput(visibilityControlHeadingSliderInput) {
    this._visibilityControlHeadingSliderInput = visibilityControlHeadingSliderInput;
  }

  /**
   * 헤딩 최소값 텍스트 요소.<br>
   * Heading minimum text element.
   * @readonly
   * @type {Element}
   */
  get visibilityControlHeadingMinValue() {
    return this._visibilityControlHeadingMinValue;
  }
  set visibilityControlHeadingMinValue(visibilityControlHeadingMinValue) {
    this._visibilityControlHeadingMinValue = visibilityControlHeadingMinValue;
  }

  /**
   * 헤딩 최대값 텍스트 요소.<br>
   * Heading maximum text element.
   * @readonly
   * @type {Element}
   */
  get visibilityControlCurrentMinValue() {
    return this._visibilityControlCurrentMinValue;
  }
  set visibilityControlHeadingCurrentValue(
    visibilityControlHeadingCurrentValue
  ) {
    this._visibilityControlHeadingCurrentValue = visibilityControlHeadingCurrentValue;
  }

  /**
   * 헤딩 현재값 텍스트 요소.<br>
   * Heading current text element.
   * @readonly
   * @type {Element}
   */
  get visibilityControlHeadingCurrentValue() {
    return this._visibilityControlHeadingCurrentValue;
  }
  set visibilityControlHeadingMaxValue(visibilityControlHeadingMaxValue) {
    this._visibilityControlHeadingMaxValue = visibilityControlHeadingMaxValue;
  }

  /**
   * 피치 마이너스 버튼 요소.<br>
   * Pitch minus button element.
   * @readonly
   * @type {Element}
   */
  get visibilityControlPitchMinusP() {
    return this._visibilityControlPitchMinusP;
  }
  set visibilityControlPitchMinusP(visibilityControlPitchMinusP) {
    this._visibilityControlPitchMinusP = visibilityControlPitchMinusP;
  }

  /**
   * 피치 플러스 버튼 요소.<br>
   * Pitch plus button element.
   * @readonly
   * @type {Element}
   */
  get visibilityControlPitchPlusP() {
    return this._visibilityControlPitchPlusP;
  }
  set visibilityControlPitchPlusP(visibilityControlPitchPlusP) {
    this._visibilityControlPitchPlusP = visibilityControlPitchPlusP;
  }

  /**
   * 피치 슬라이더.<br>
   * Pitch slider element.
   * @readonly
   * @type {Element}
   */
  get visibilityControlPitchSliderInput() {
    return this._visibilityControlPitchSliderInput;
  }
  set visibilityControlPitchSliderInput(visibilityControlPitchSliderInput) {
    this._visibilityControlPitchSliderInput = visibilityControlPitchSliderInput;
  }

  /**
   * 피치 최소값 텍스트 요소.<br>
   * Pitch minimum text element.
   * @readonly
   * @type {Element}
   */
  get visibilityControlPitchMinValue() {
    return this._visibilityControlPitchMinValue;
  }
  set visibilityControlPitchMinValue(visibilityControlPitchMinValue) {
    this._visibilityControlPitchMinValue = visibilityControlPitchMinValue;
  }

  /**
   * 피치 현재값 텍스트 요소.<br>
   * Pitch current text element.
   * @readonly
   * @type {Element}
   */
  get visibilityControlPitchCurrentValue() {
    return this._visibilityControlPitchCurrentValue;
  }
  set visibilityControlPitchCurrentValue(visibilityControlPitchCurrentValue) {
    this._visibilityControlPitchCurrentValue = visibilityControlPitchCurrentValue;
  }

  /**
   * 피치 최대값 텍스트 요소.<br>
   * Pitch maximum text
   * @readonly
   * @type {Element}
   */
  get visibilityControlPitchMaxValue() {
    return this._visibilityControlPitchMaxValue;
  }
  set visibilityControlPitchMaxValue(visibilityControlPitchMaxValue) {
    this._visibilityControlPitchMaxValue = visibilityControlPitchMaxValue;
  }

  /**
   * 시야 각 마이너스 버튼 요소.<br>
   * Fov minus button element.
   * @readonly
   * @type {Element}
   */
  get visibilityControlFovMinusP() {
    return this._visibilityControlFovMinusP;
  }
  set visibilityControlFovMinusP(visibilityControlFovMinusP) {
    this._visibilityControlFovMinusP = visibilityControlFovMinusP;
  }

  /**
   * 화각 플러스 버튼 요소.<br>
   * Fov plus button element.
   * @readonly
   * @type {Element}
   */
  get visibilityControlFovPlusP() {
    return this._visibilityControlFovPlusP;
  }
  set visibilityControlFovPlusP(visibilityControlFovPlusP) {
    this._visibilityControlFovPlusP = visibilityControlFovPlusP;
  }

  /**
   * 화각 슬라이더.<br>
   * Fov silder element.
   * @readonly
   * @type {Element}
   */
  get visibilityControlFovSliderInput() {
    return this._visibilityControlFovSliderInput;
  }
  set visibilityControlFovSliderInput(visibilityControlFovSliderInput) {
    this._visibilityControlFovSliderInput = visibilityControlFovSliderInput;
  }

  /**
   * 화각 최소값 텍스트 요소.<br>
   * Fov minimum text element.
   * @readonly
   * @type {Element}
   */
  get visibilityControlFovMinValue() {
    return this._visibilityControlFovMinValue;
  }
  set visibilityControlFovMinValue(visibilityControlFovMinValue) {
    this._visibilityControlFovMinValue = visibilityControlFovMinValue;
  }

  /**
   * 화각 현재값 텍스트 요소.<br>
   * Fov current text element.
   * @readonly
   * @type {Element}
   */
  get visibilityControlFovCurrentValue() {
    return this._visibilityControlFovCurrentValue;
  }
  set visibilityControlFovCurrentValue(visibilityControlFovCurrentValue) {
    this._visibilityControlFovCurrentValue = visibilityControlFovCurrentValue;
  }

  /**
   * 화각 최대값 텍스트 요소.<br>
   * Fov maximum text element.
   * @readonly
   * @type {Element}
   */
  get visibilityControlFovMaxValue() {
    return this._visibilityControlFovMaxValue;
  }
  set visibilityControlFovMaxValue(visibilityControlFovMaxValue) {
    this._visibilityControlFovMaxValue = visibilityControlFovMaxValue;
  }

  /**
   * 종횡비 마이너스 버튼 요소.<br>
   * AspectRatio minus button element.
   * @readonly
   * @type {Element}
   */
  get visibilityControlAspectRatioMinusP() {
    return this._visibilityControlAspectRatioMinusP;
  }
  set visibilityControlAspectRatioMinusP(visibilityControlAspectRatioMinusP) {
    this._visibilityControlAspectRatioMinusP = visibilityControlAspectRatioMinusP;
  }

  /**
   * 종횡비 플러스 버튼 요소.<br>
   * AspectRatio plus button element.
   * @readonly
   * @type {Element}
   */
  get visibilityControlAspectRatioPlusP() {
    return this._visibilityControlAspectRatioPlusP;
  }
  set visibilityControlAspectRatioPlusP(visibilityControlAspectRatioPlusP) {
    this._visibilityControlAspectRatioPlusP = visibilityControlAspectRatioPlusP;
  }

  /**
   * 종횡비 슬라이더 요소.<br>
   * AspectRatio slider element.
   * @readonly
   * @type {Element}
   */
  get visibilityControlAspectRatioSliderInput() {
    return this._visibilityControlAspectRatioSliderInput;
  }
  set visibilityControlAspectRatioSliderInput(
    visibilityControlAspectRatioSliderInput
  ) {
    this._visibilityControlAspectRatioSliderInput = visibilityControlAspectRatioSliderInput;
  }

  /**
   * 종횡비 최소값 텍스트 요소.<br>
   * AspectRatio minimum text element.
   * @readonly
   * @type {Element}
   */
  get visibilityControlAspectRatioMinValue() {
    return this._visibilityControlAspectRatioMinValue;
  }
  set visibilityControlAspectRatioMinValue(
    visibilityControlAspectRatioMinValue
  ) {
    this._visibilityControlAspectRatioMinValue = visibilityControlAspectRatioMinValue;
  }

  /**
   * 종횡비 현재값 텍스트 요소.<br>
   * AspectRatio current text element.
   * @readonly
   * @type {Element}
   */
  get visibilityControlAspectRatioCurrentValue() {
    return this._visibilityControlAspectRatioCurrentValue;
  }
  set visibilityControlAspectRatioCurrentValue(
    visibilityControlAspectRatioCurrentValue
  ) {
    this._visibilityControlAspectRatioCurrentValue = visibilityControlAspectRatioCurrentValue;
  }

  /**
   * 종횡비 최대값 텍스트 요소.<br>
   * AspectRatio maximum text element.
   * @readonly
   * @type {Element}
   */
  get visibilityControlAspectRatioMaxValue() {
    return this._visibilityControlAspectRatioMaxValue;
  }
  set visibilityControlAspectRatioMaxValue(
    visibilityControlAspectRatioMaxValue
  ) {
    this._visibilityControlAspectRatioMaxValue = visibilityControlAspectRatioMaxValue;
  }

  /**
   * 가시거리 마이너스 버튼 요소.<br>
   * Distance minus button element.
   * @readonly
   * @type {Element}
   */
  get visibilityControlDistanceMinusP() {
    return this._visibilityControlDistanceMinusP;
  }
  set visibilityControlDistanceMinusP(visibilityControlDistanceMinusP) {
    this._visibilityControlDistanceMinusP = visibilityControlDistanceMinusP;
  }

  /**
   * 가시거리 플러스 버튼 요소.<br>
   * Distance plus button element.
   * @readonly
   * @type {Element}
   */
  get visibilityControlDistancePlusP() {
    return this._visibilityControlDistancePlusP;
  }
  set visibilityControlDistancePlusP(visibilityControlDistancePlusP) {
    this._visibilityControlDistancePlusP = visibilityControlDistancePlusP;
  }

  /**
   * 가시거리 슬라이더 요소.<br>
   * Distance slider element.
   * @readonly
   * @type {Element}
   */
  get visibilityControlDistanceSliderInput() {
    return this._visibilityControlDistanceSliderInput;
  }
  set visibilityControlDistanceSliderInput(
    visibilityControlDistanceSliderInput
  ) {
    this._visibilityControlDistanceSliderInput = visibilityControlDistanceSliderInput;
  }

  /**
   * 가시거리 최소값 텍스트 요소.<br>
   * Distance minimum text element.
   * @readonly
   * @type {Element}
   */
  get visibilityControlDistanceMinValue() {
    return this._visibilityControlDistanceMinValue;
  }
  set visibilityControlDistanceMinValue(visibilityControlDistanceMinValue) {
    this._visibilityControlDistanceMinValue = visibilityControlDistanceMinValue;
  }

  /**
   * 가시거리 현재값 텍스트 요소.<br>
   * Distance current text element.
   * @readonly
   * @type {Element}
   */
  get visibilityControlDistanceCurrentValue() {
    return this._visibilityControlDistanceCurrentValue;
  }
  set visibilityControlDistanceCurrentValue(
    visibilityControlDistanceCurrentValue
  ) {
    this._visibilityControlDistanceCurrentValue = visibilityControlDistanceCurrentValue;
  }

  /**
   * 가시거리 최대값 텍스트 요소.<br>
   * Distance maximum text element.
   * @readonly
   * @type {Element}
   */
  get visibilityControlDistanceMaxValue() {
    return this._visibilityControlDistanceMaxValue;
  }
  set visibilityControlDistanceMaxValue(visibilityControlDistanceMaxValue) {
    this._visibilityControlDistanceMaxValue = visibilityControlDistanceMaxValue;
  }

  /**
   * 팝업 오픈.<br>
   * Open popup.
   */
  open() {
    this.update();

    // 부모 자식으로 wrapper 추가
    if (this.parent instanceof Element) {
      this.parent.append(this.wrapper);
    }

    this.wrapper.classList.remove("hide");
    this.ditapPaneManager?.addPane(this.wrapper);

    this.createResizeEvent();

    const mobileLabel = this.parent.getElementsByClassName(
      "ditap-mobile-label"
    );
    if (mobileLabel.length > 0) {
      Utils.moveUnderLabel(this.parent);
    }
  }

  /**
   * 팝업 파괴.<br>
   * Destory popup.
   */
  destroy() {
    super.destroy();
  }

  /**
   * 팝업 업데이트.<br>
   * Update popup.
   */
  update() {
    // 수평회전
    const heading = this.analysisVisibility.heading;
    this.setHeadingSliderValue(heading);
    this.setHeadingCurrentValue(heading);

    // 수직회전
    const pitch = this.analysisVisibility.pitch;
    this.setPitchSliderValue(pitch);
    this.setPitchCurrentValue(pitch);

    // 화각
    const fov = this.analysisVisibility.fov;
    this.setFovSliderValue(fov);
    this.setFovCurrentValue(fov);

    // 종횡비
    const aspectRatio = this.analysisVisibility.aspectRatio;
    this.setAspectRatioSliderValue(aspectRatio);
    this.setAspectRatioCurrentValue(aspectRatio);

    // 가시거리
    const distance = this.analysisVisibility.distance;
    this.setDistanceSliderValue(distance);
    this.setDistanceCurrentValue(distance);
  }

  /**
   * 헤딩 슬라이더 값 변경.<br>
   * Set heading slider value.
   * @param {number} value 헤딩 각도 (도). </br>Heading value (degrees).
   */
  setHeadingSliderValue(value) {
    this.visibilityControlHeadingSliderInput.value = value;
    slider(this.visibilityControlHeadingSliderInput);
  }

  /**
   * 헤딩 현재값 변경.<br>
   * Set heading current value.
   * @param {number} value 헤딩 각도 (도). </br>Heading value (degrees).
   */
  setHeadingCurrentValue(value) {
    this.visibilityControlHeadingCurrentValue.innerText = value;
  }

  /**
   * 피치 슬라이더 값 변경.<br>
   * Set pitch slider value.
   * @param {number} value 피치 각도 (도). </br>Pitch value (degrees).
   */
  setPitchSliderValue(value) {
    this.visibilityControlPitchSliderInput.value = value;
    slider(this.visibilityControlPitchSliderInput);
  }

  /**
   * 피치 현재값 변경.<br>
   * Set pitch current value.
   * @param {number} value 피치 각도 (도). </br>Pitch value (degrees).
   */
  setPitchCurrentValue(value) {
    this.visibilityControlPitchCurrentValue.innerText = value;
  }

  /**
   * 화각 슬라이더 값 변경.<br>
   * Set fov slider value.
   * @param {number} value 화각 (도). </br>Fov value (degrees).
   */
  setFovSliderValue(value) {
    this.visibilityControlFovSliderInput.value = value;
    slider(this.visibilityControlFovSliderInput);
  }

  /**
   * 화각 현재값 변경.<br>
   * Set fov current value.
   * @param {number} value 화각 (도). </br>Fov value (degrees).
   */
  setFovCurrentValue(value) {
    this.visibilityControlFovCurrentValue.innerText = value;
  }

  /**
   * 종횡비 슬라이더 값 변경.<br>
   * Set AspectRatio slider value.
   * @param {number} value 종횡비 (가로/세로). </br>AspectRatio value (width/height).
   */
  setAspectRatioSliderValue(value) {
    this.visibilityControlAspectRatioSliderInput.value = value;
    slider(this.visibilityControlAspectRatioSliderInput);
  }

  /**
   * 종횡비 현재값 변경.<br>
   * Set AspectRatio current value.
   * @param {number} value 종횡비 (가로/세로). </br>AspectRatio value (width/height).
   */
  setAspectRatioCurrentValue(value) {
    this.visibilityControlAspectRatioCurrentValue.innerText = value;
  }

  /**
   * 가시거리 슬라이더 값 변경.<br>
   * Set distance slider value.
   * @param {number} value 가시거리 (미터). </br>Distance value (meters).
   */
  setDistanceSliderValue(value) {
    this.visibilityControlDistanceSliderInput.value = value;
    slider(this.visibilityControlDistanceSliderInput);
  }

  /**
   * 가시거리 현재값 변경.<br>
   * Set distance current value.
   * @param {number} value 가시거리 (미터). </br>Distance value (meters).
   */
  setDistanceCurrentValue(value) {
    this.visibilityControlDistanceCurrentValue.innerText = value;
  }

  /*
   * 팝업 이벤트 설정.<br>
   * Set popup event
   */
  setEvent() {
    //# 수평회전
    // 슬라이더
    this.visibilityControlHeadingSliderInput.addEventListener("input", () => {
      //this.analysisVisibility.heading = Number(this.visibilityControlHeadingSliderInput.value);
      let value = Number(this.visibilityControlHeadingSliderInput.value);
      value = Number(value.toFixed(1));
      this.analysisVisibility.setHeading(value);
      this.setHeadingCurrentValue(value);
      slider(this.visibilityControlHeadingSliderInput);
    });
    // - 버튼
    this.visibilityControlHeadingMinusP.addEventListener("click", () => {
      let value = Number(this.visibilityControlHeadingSliderInput.value) - 0.1;
      value = Number(value.toFixed(1));
      if (value < -180) {
        value = -180;
      } else if (180 < value) {
        value = 180;
      }
      this.analysisVisibility.setHeading(value);
      this.setHeadingSliderValue(value);
      this.setHeadingCurrentValue(value);
    });
    // + 버튼
    this.visibilityControlHeadingPlusP.addEventListener("click", () => {
      let value = Number(this.visibilityControlHeadingSliderInput.value) + 0.1;
      value = Number(value.toFixed(1));
      if (value < -180) {
        value = -180;
      } else if (180 < value) {
        value = 180;
      }
      this.analysisVisibility.setHeading(value);
      this.setHeadingSliderValue(value);
      this.setHeadingCurrentValue(value);
    });

    //# 수직회전
    // 슬라이더
    this.visibilityControlPitchSliderInput.addEventListener("input", () => {
      //this.analysisVisibility.pitch = Number(this.visibilityControlPitchSliderInput.value);
      let value = Number(this.visibilityControlPitchSliderInput.value);
      value = Number(value.toFixed(1));
      this.analysisVisibility.setPitch(value);
      this.setPitchCurrentValue(value);
      slider(this.visibilityControlPitchSliderInput);
    });
    // - 버튼
    this.visibilityControlPitchMinusP.addEventListener("click", () => {
      let value = Number(this.visibilityControlPitchSliderInput.value) - 0.1;
      value = Number(value.toFixed(1));
      if (value < -180) {
        value = -180;
      } else if (180 < value) {
        value = 180;
      }
      this.analysisVisibility.setPitch(value);
      this.setPitchSliderValue(value);
      this.setPitchCurrentValue(value);
    });
    // + 버튼
    this.visibilityControlPitchPlusP.addEventListener("click", () => {
      let value = Number(this.visibilityControlPitchSliderInput.value) + 0.1;
      value = Number(value.toFixed(1));
      if (value < -180) {
        value = -180;
      } else if (180 < value) {
        value = 180;
      }
      this.analysisVisibility.setPitch(value);
      this.setPitchSliderValue(value);
      this.setPitchCurrentValue(value);
    });

    //# 화각
    // 슬라이더
    this.visibilityControlFovSliderInput.addEventListener("input", () => {
      //this.analysisVisibility.fov = Number(this.visibilityControlFovSliderInput.value);
      let value = Number(this.visibilityControlFovSliderInput.value);
      value = Number(value.toFixed(1));
      if (value <= 0) {
        value = 0;
        this.analysisVisibility.setFov(0.1);
      } else if (180 <= value) {
        value = 180;
        this.analysisVisibility.setFov(179.9);
      } else {
        this.analysisVisibility.setFov(value);
      }

      this.setFovCurrentValue(value);
      slider(this.visibilityControlFovSliderInput);
    });
    // - 버튼
    this.visibilityControlFovMinusP.addEventListener("click", () => {
      let value = Number(this.visibilityControlFovSliderInput.value) - 0.1;
      value = Number(value.toFixed(1));
      if (value <= 0) {
        value = 0;
        this.analysisVisibility.setFov(0.1);
      } else if (180 <= value) {
        value = 180;
        this.analysisVisibility.setFov(179.9);
      } else {
        this.analysisVisibility.setFov(value);
      }

      this.setFovSliderValue(value);
      this.setFovCurrentValue(value);
    });
    // + 버튼
    this.visibilityControlFovPlusP.addEventListener("click", () => {
      let value = Number(this.visibilityControlFovSliderInput.value) + 0.1;
      value = Number(value.toFixed(1));
      if (value < 0) {
        value = 0;
      } else if (180 < value) {
        value = 180;
      }
      this.analysisVisibility.setFov(value === 180 ? 179.9 : value);
      this.setFovSliderValue(value);
      this.setFovCurrentValue(value);
    });

    //# 종횡비
    // 슬라이더
    this.visibilityControlAspectRatioSliderInput.addEventListener(
      "input",
      () => {
        //this.analysisVisibility.fov = Number(this.visibilityControlAspectRatioSliderInput.value);
        let value = Number(this.visibilityControlAspectRatioSliderInput.value);
        value = Number(value.toFixed(1));
        this.analysisVisibility.setAspectRatio(value);
        this.setAspectRatioCurrentValue(value);
        slider(this.visibilityControlAspectRatioSliderInput);
      }
    );
    // - 버튼
    this.visibilityControlAspectRatioMinusP.addEventListener("click", () => {
      let value =
        Number(this.visibilityControlAspectRatioSliderInput.value) - 0.1;
      value = Number(value.toFixed(1));
      if (value < 0.1) {
        value = 0.1;
      } else if (10 < value) {
        value = 10;
      }
      this.analysisVisibility.setAspectRatio(value);
      this.setAspectRatioSliderValue(value);
      this.setAspectRatioCurrentValue(value);
    });
    // + 버튼
    this.visibilityControlAspectRatioPlusP.addEventListener("click", () => {
      let value =
        Number(this.visibilityControlAspectRatioSliderInput.value) + 0.1;
      value = Number(value.toFixed(1));
      if (value < 0.1) {
        value = 0.1;
      } else if (10 < value) {
        value = 10;
      }
      this.analysisVisibility.setAspectRatio(value);
      this.setAspectRatioSliderValue(value);
      this.setAspectRatioCurrentValue(value);
    });

    //# 가시거리
    // 슬라이더
    this.visibilityControlDistanceSliderInput.addEventListener("input", () => {
      //this.analysisVisibility.distance = Number(this.visibilityControlDistanceSliderInput.value);
      let value = Number(this.visibilityControlDistanceSliderInput.value);
      value = Number(value.toFixed(1));
      this.analysisVisibility.setDistance(value);
      this.setDistanceCurrentValue(value);
      slider(this.visibilityControlDistanceSliderInput);
    });
    // - 버튼
    this.visibilityControlDistanceMinusP.addEventListener("click", () => {
      let value = Number(this.visibilityControlDistanceSliderInput.value) - 0.1;
      value = Number(value.toFixed(1));
      if (value < 0.1) {
        value = 0.1;
      } else if (1000 < value) {
        value = 1000;
      }
      this.analysisVisibility.setDistance(value);
      this.setDistanceSliderValue(value);
      this.setDistanceCurrentValue(value);
    });
    // + 버튼
    this.visibilityControlDistancePlusP.addEventListener("click", () => {
      let value = Number(this.visibilityControlDistanceSliderInput.value) + 0.1;
      value = Number(value.toFixed(1));
      if (value < 0.1) {
        value = 0.1;
      } else if (1000 < value) {
        value = 1000;
      }
      this.analysisVisibility.setDistance(value);
      this.setDistanceSliderValue(value);
      this.setDistanceCurrentValue(value);
    });
  }

  // reset() {
  //   // 수평회전
  //   const heading = analysisVisibility.options.heading;
  //   this.setHeadingSliderValue(heading);
  //   this.setHeadingCurrentValue(heading);

  //   // 수직회전
  //   const pitch = analysisVisibility.options.pitch;
  //   this.setPitchSliderValue(pitch);
  //   this.setPitchCurrentValue(pitch);

  //   // 화각
  //   const fov = analysisVisibility.options.roll;
  //   this.setFovSliderValue(fov);
  //   this.setFovCurrentValue(fov);

  //   // 종횡비
  //   const aspectRatio = analysisVisibility.options.aspectRatio;
  //   this.setAspectRatioSliderValue(aspectRatio);
  //   this.setAspectRatioCurrentValue(aspectRatio);

  //   // 가시거리
  //   const distance = analysisVisibility.options.distance;
  //   this.setDistanceSliderValue(distance);
  //   this.setDistanceCurrentValue(distance);
  // }

  /*
   * 팝업 생성.<br>
   * Create popup
   * @returns {Object} 생성된 팝업 속성 객체.<br> The attribute object of the generated popup element.
   * @example
   * # 가시성 분석 팝업
   * {
      header: {
        title: title - 타이틀
      },
      content: {
        subTitle: 서브타이틀,

        visibilityControlList: 수평회전, 수직회전, 화각, 종횡비, 가시거리 Wrapper,

        visibilityControlHeadingMinusP: 수평회전 - 버튼,
        visibilityControlHeadingPlusP: 수평회전 + 버튼,
        visibilityControlHeadingSliderInput: 수평회전 슬라이더 Input,
        visibilityControlHeadingMinValue: 수평회전 최소값 텍스트,
        visibilityControlHeadingMaxValue: 수평회전 최대값 텍스트,

        visibilityControlPitchMinusP: 수직회전 - 버튼,
        visibilityControlPitchPlusP: 수직회전 + 버튼,
        visibilityControlPitchSliderInput: 수직회전 슬라이더 Input,
        visibilityControlPitchMinValue: 수직회전 최소값 텍스트,
        visibilityControlPitchMaxValue: 수직회전 최대값 텍스트,

        visibilityControlFovMinusP: 화각 - 버튼,
        visibilityControlFovPlusP: 화각 + 버튼,
        visibilityControlFovSliderInput: 화각 슬라이더 Input,
        visibilityControlFovMinValue: 화각 최소값 텍스트,
        visibilityControlFovMaxValue: 화각 최대값 텍스트,

        visibilityControlAspectRatioMinusP: 종횡비 - 버튼,
        visibilityControlAspectRatioPlusP: 종횡비 + 버튼,
        visibilityControlAspectRatioSliderInput: 종횡비 슬라이더 Input,
        visibilityControlAspectRatioMinValue: 종횡비 최소값 텍스트,
        visibilityControlAspectRatioMaxValue: 종횡비 최대값 텍스트,

        visibilityControlDistanceMinusP: 가시거리 - 버튼,
        visibilityControlDistancePlusP: 가시거리 + 버튼,
        visibilityControlDistanceSliderInput: 가시거리 슬라이더 Input,
        visibilityControlDistanceMinValue: 가시거리 최소값 텍스트,
        visibilityControlDistanceMaxValue: 가시거리 최대값 텍스트,
      },
    };
   */
  createInfoBox() {
    this.wrapper.classList.add(
      "ditap-popup-mobile",
      "ditap-visibility-popup",
      "ditap-mobile-bottom",
      "header-top",
      "draggable"
    );
    // header
    const title = document.createElement("h2");
    title.classList.add("ditap-popup-title");
    title.innerText = "시점 조절";

    // content
    const subTitle = document.createElement("p");
    subTitle.classList.add("ditap-popup-subtitle");
    subTitle.innerText = "아래의 시점을 조정해주세요";
    const visibilityControlList = document.createElement("ul");
    visibilityControlList.classList.add("ditap-control-list");

    // <div class="ditap-style-inp-wrap ditap-style-select js-ditap-style-select">
    //     <div class="ditap-style-inp js-ditap-style-type">원형</div>
    //     <p class="ditap-dropbox-img"></p>
    //     <div class="ditap-style-opt js-ditap-style-type-opt hide">
    //       <ul class="ditap-style-opt-list">
    //       <li class="ditap-style-opt-title js-ditap-style-type-opt-circle-btn">원형</li>
    //       <li class="ditap-style-opt-title js-ditap-style-type-opt-icon-btn">아이콘</li>
    //       </ul>
    //     </div>
    //   </div>

    const visibilityControlHeadingItem = document.createElement("li");
    visibilityControlHeadingItem.classList.add("ditap-control-item");
    const visibilityControlHeadingHeader = document.createElement("div");
    visibilityControlHeadingHeader.classList.add("ditap-control-header");
    const visibilityControlHeadingMinusP = document.createElement("p");
    visibilityControlHeadingMinusP.classList.add("ditap-control-minus");
    const visibilityControlHeadingMinusButton = document.createElement(
      "button"
    );
    visibilityControlHeadingMinusButton.classList.add("ditap-control-img");
    const visibilityControlHeadingTitle = document.createElement("p");
    visibilityControlHeadingTitle.classList.add("ditap-control-title");
    visibilityControlHeadingTitle.innerText = "수평회전";
    const visibilityControlHeadingPlusP = document.createElement("p");
    visibilityControlHeadingPlusP.classList.add("ditap-control-plus");
    const visibilityControlHeadingPlusButton = document.createElement("button");
    visibilityControlHeadingPlusButton.classList.add("ditap-control-img");
    const visibilityControlHeadingSlider = document.createElement("div");
    visibilityControlHeadingSlider.classList.add("ditap-slider-item");
    const visibilityControlHeadingSliderLabel = document.createElement("label");
    const visibilityControlHeadingSliderInput = document.createElement("input");
    visibilityControlHeadingSliderInput.setAttribute("type", "range");
    visibilityControlHeadingSliderInput.setAttribute("name", "slider");
    visibilityControlHeadingSliderInput.setAttribute("min", -180);
    visibilityControlHeadingSliderInput.setAttribute("max", 180);
    visibilityControlHeadingSliderInput.setAttribute("value", 0);
    visibilityControlHeadingSliderInput.setAttribute("step", 0.1);
    visibilityControlHeadingSliderInput.classList.add("ditap-slider");
    const visibilityControlHeadingValue = document.createElement("div");
    visibilityControlHeadingValue.classList.add("ditap-control-footer");
    const visibilityControlHeadingMinValue = document.createElement("p");
    visibilityControlHeadingMinValue.classList.add("ditap-control-value");
    visibilityControlHeadingMinValue.innerText = -180;
    const visibilityControlHeadingCurrentValue = document.createElement("p");
    visibilityControlHeadingCurrentValue.classList.add(
      "ditap-control-value",
      "ditap-control-center-value"
    );
    visibilityControlHeadingCurrentValue.innerText = 0;
    const visibilityControlHeadingMaxValue = document.createElement("p");
    visibilityControlHeadingMaxValue.classList.add(
      "ditap-control-value",
      "ditap-control-right-value"
    );
    visibilityControlHeadingMaxValue.innerText = 180;
    // visibilityControlHeadingMinusP
    visibilityControlHeadingMinusP.append(visibilityControlHeadingMinusButton);
    // visibilityControlHeadingPlusP
    visibilityControlHeadingPlusP.append(visibilityControlHeadingPlusButton);
    // visibilityControlHeadingSliderLabel
    visibilityControlHeadingSliderLabel.append(
      visibilityControlHeadingSliderInput
    );
    // visibilityControlHeadingHeader
    visibilityControlHeadingHeader.append(
      visibilityControlHeadingMinusP,
      visibilityControlHeadingTitle,
      visibilityControlHeadingPlusP
    );
    // visibilityControlHeadingSlider
    visibilityControlHeadingSlider.append(visibilityControlHeadingSliderLabel);
    // visibilityControlHeadingValue
    visibilityControlHeadingValue.append(
      visibilityControlHeadingMinValue,
      visibilityControlHeadingCurrentValue,
      visibilityControlHeadingMaxValue
    );
    // visibilityControlHeadingItem
    visibilityControlHeadingItem.append(
      visibilityControlHeadingHeader,
      visibilityControlHeadingSlider,
      visibilityControlHeadingValue
    );

    const visibilityControlPitchItem = document.createElement("li");
    visibilityControlPitchItem.classList.add("ditap-control-item");
    const visibilityControlPitchHeader = document.createElement("div");
    visibilityControlPitchHeader.classList.add("ditap-control-header");
    const visibilityControlPitchMinusP = document.createElement("p");
    visibilityControlPitchMinusP.classList.add("ditap-control-minus");
    const visibilityControlPitchMinusButton = document.createElement("button");
    visibilityControlPitchMinusButton.classList.add("ditap-control-img");
    const visibilityControlPitchTitle = document.createElement("p");
    visibilityControlPitchTitle.classList.add("ditap-control-title");
    visibilityControlPitchTitle.innerText = "수직회전";
    const visibilityControlPitchPlusP = document.createElement("p");
    visibilityControlPitchPlusP.classList.add("ditap-control-plus");
    const visibilityControlPitchPlusButton = document.createElement("button");
    visibilityControlPitchPlusButton.classList.add("ditap-control-img");
    const visibilityControlPitchSlider = document.createElement("div");
    visibilityControlPitchSlider.classList.add("ditap-slider-item");
    const visibilityControlPitchSliderLabel = document.createElement("label");
    const visibilityControlPitchSliderInput = document.createElement("input");
    visibilityControlPitchSliderInput.classList.add("ditap-slider");
    visibilityControlPitchSliderInput.setAttribute("name", "slider");
    visibilityControlPitchSliderInput.setAttribute("type", "range");
    visibilityControlPitchSliderInput.setAttribute("min", -180);
    visibilityControlPitchSliderInput.setAttribute("max", 180);
    visibilityControlPitchSliderInput.setAttribute("value", 180);
    visibilityControlPitchSliderInput.setAttribute("step", 0.1);
    const visibilityControlPitchValue = document.createElement("div");
    visibilityControlPitchValue.classList.add("ditap-control-footer");
    const visibilityControlPitchMinValue = document.createElement("p");
    visibilityControlPitchMinValue.classList.add("ditap-control-value");
    visibilityControlPitchMinValue.innerText = -180;
    const visibilityControlPitchCurrentValue = document.createElement("p");
    visibilityControlPitchCurrentValue.classList.add(
      "ditap-control-value",
      "ditap-control-center-value"
    );
    visibilityControlPitchCurrentValue.innerText = 0;
    const visibilityControlPitchMaxValue = document.createElement("p");
    visibilityControlPitchMaxValue.classList.add(
      "ditap-control-value",
      "ditap-control-right-value"
    );
    visibilityControlPitchMaxValue.innerText = 180;
    // visibilityControlPitchMinusP
    visibilityControlPitchMinusP.append(visibilityControlPitchMinusButton);
    // visibilityControlPitchPlusP
    visibilityControlPitchPlusP.append(visibilityControlPitchPlusButton);
    // visibilityControlPitchSliderLabel
    visibilityControlPitchSliderLabel.append(visibilityControlPitchSliderInput);
    // visibilityControlPitchHeader
    visibilityControlPitchHeader.append(
      visibilityControlPitchMinusP,
      visibilityControlPitchTitle,
      visibilityControlPitchPlusP
    );
    // visibilityControlPitchSlider
    visibilityControlPitchSlider.append(visibilityControlPitchSliderLabel);
    // visibilityControlPitchValue
    visibilityControlPitchValue.append(
      visibilityControlPitchMinValue,
      visibilityControlPitchCurrentValue,
      visibilityControlPitchMaxValue
    );
    // visibilityControlPitchItem
    visibilityControlPitchItem.append(
      visibilityControlPitchHeader,
      visibilityControlPitchSlider,
      visibilityControlPitchValue
    );

    const visibilityControlFovItem = document.createElement("li");
    visibilityControlFovItem.classList.add("ditap-control-item");
    const visibilityControlFovHeader = document.createElement("div");
    visibilityControlFovHeader.classList.add("ditap-control-header");
    const visibilityControlFovMinusP = document.createElement("p");
    visibilityControlFovMinusP.classList.add("ditap-control-minus");
    const visibilityControlFovMinusButton = document.createElement("button");
    visibilityControlFovMinusButton.classList.add("ditap-control-img");
    const visibilityControlFovTitle = document.createElement("p");
    visibilityControlFovTitle.classList.add("ditap-control-title");
    visibilityControlFovTitle.innerText = "화각";
    const visibilityControlFovPlusP = document.createElement("p");
    visibilityControlFovPlusP.classList.add("ditap-control-plus");
    const visibilityControlFovPlusButton = document.createElement("button");
    visibilityControlFovPlusButton.classList.add("ditap-control-img");
    const visibilityControlFovSlider = document.createElement("div");
    visibilityControlFovSlider.classList.add("ditap-slider-item");
    const visibilityControlFovSliderLabel = document.createElement("label");
    const visibilityControlFovSliderInput = document.createElement("input");
    visibilityControlFovSliderInput.classList.add("ditap-slider");
    visibilityControlFovSliderInput.setAttribute("type", "range");
    visibilityControlFovSliderInput.setAttribute("name", "slider");
    visibilityControlFovSliderInput.setAttribute("min", 0);
    visibilityControlFovSliderInput.setAttribute("max", 180);
    visibilityControlFovSliderInput.setAttribute("value", 90);
    visibilityControlFovSliderInput.setAttribute("step", 0.1);
    const visibilityControlFovValue = document.createElement("div");
    visibilityControlFovValue.classList.add("ditap-control-footer");
    const visibilityControlFovMinValue = document.createElement("p");
    visibilityControlFovMinValue.classList.add("ditap-control-value");
    visibilityControlFovMinValue.innerText = 0;
    const visibilityControlFovCurrentValue = document.createElement("p");
    visibilityControlFovCurrentValue.classList.add(
      "ditap-control-value",
      "ditap-control-center-value"
    );
    visibilityControlFovCurrentValue.innerText = 90;
    const visibilityControlFovMaxValue = document.createElement("p");
    visibilityControlFovMaxValue.classList.add(
      "ditap-control-value",
      "ditap-control-right-value"
    );
    visibilityControlFovMaxValue.innerText = 180;
    // visibilityControlFovMinusP
    visibilityControlFovMinusP.append(visibilityControlFovMinusButton);
    // visibilityControlFovPlusP
    visibilityControlFovPlusP.append(visibilityControlFovPlusButton);
    // visibilityControlFovSliderLabel
    visibilityControlFovSliderLabel.append(visibilityControlFovSliderInput);
    // visibilityControlFovHeader
    visibilityControlFovHeader.append(
      visibilityControlFovMinusP,
      visibilityControlFovTitle,
      visibilityControlFovPlusP
    );
    // visibilityControlFovSlider
    visibilityControlFovSlider.append(visibilityControlFovSliderLabel);
    // visibilityControlFovValue
    visibilityControlFovValue.append(
      visibilityControlFovMinValue,
      visibilityControlFovCurrentValue,
      visibilityControlFovMaxValue
    );
    // visibilityControlFovItem
    visibilityControlFovItem.append(
      visibilityControlFovHeader,
      visibilityControlFovSlider,
      visibilityControlFovValue
    );

    const visibilityControlAspectRatioItem = document.createElement("li");
    visibilityControlAspectRatioItem.classList.add("ditap-control-item");
    const visibilityControlAspectRatioHeader = document.createElement("div");
    visibilityControlAspectRatioHeader.classList.add("ditap-control-header");
    const visibilityControlAspectRatioMinusP = document.createElement("p");
    visibilityControlAspectRatioMinusP.classList.add("ditap-control-minus");
    const visibilityControlAspectRatioMinusButton = document.createElement(
      "button"
    );
    visibilityControlAspectRatioMinusButton.classList.add("ditap-control-img");
    const visibilityControlAspectRatioTitle = document.createElement("p");
    visibilityControlAspectRatioTitle.classList.add("ditap-control-title");
    visibilityControlAspectRatioTitle.innerText = "종횡비";
    const visibilityControlAspectRatioPlusP = document.createElement("p");
    visibilityControlAspectRatioPlusP.classList.add("ditap-control-plus");
    const visibilityControlAspectRatioPlusButton = document.createElement(
      "button"
    );
    visibilityControlAspectRatioPlusButton.classList.add("ditap-control-img");
    const visibilityControlAspectRatioSlider = document.createElement("div");
    visibilityControlAspectRatioSlider.classList.add("ditap-slider-item");
    const visibilityControlAspectRatioSliderLabel = document.createElement(
      "label"
    );
    const visibilityControlAspectRatioSliderInput = document.createElement(
      "input"
    );
    visibilityControlAspectRatioSliderInput.classList.add("ditap-slider");
    visibilityControlAspectRatioSliderInput.setAttribute("type", "range");
    visibilityControlAspectRatioSliderInput.setAttribute("name", "slider");
    visibilityControlAspectRatioSliderInput.setAttribute("min", 0.1);
    visibilityControlAspectRatioSliderInput.setAttribute("max", 10);
    visibilityControlAspectRatioSliderInput.setAttribute("step", 0.1);
    const visibilityControlAspectRatioValue = document.createElement("div");
    visibilityControlAspectRatioValue.classList.add("ditap-control-footer");
    const visibilityControlAspectRatioMinValue = document.createElement("p");
    visibilityControlAspectRatioMinValue.classList.add("ditap-control-value");
    visibilityControlAspectRatioMinValue.innerText = 0.1;
    const visibilityControlAspectRatioCurrentValue = document.createElement(
      "p"
    );
    visibilityControlAspectRatioCurrentValue.classList.add(
      "ditap-control-value",
      "ditap-control-center-value"
    );
    visibilityControlAspectRatioCurrentValue.innerText = 1.5;
    const visibilityControlAspectRatioMaxValue = document.createElement("p");
    visibilityControlAspectRatioMaxValue.classList.add(
      "ditap-control-value",
      "ditap-control-right-value"
    );
    visibilityControlAspectRatioMaxValue.innerText = 10;
    // visibilityControlAspectRatioMinusP
    visibilityControlAspectRatioMinusP.append(
      visibilityControlAspectRatioMinusButton
    );
    // visibilityControlAspectRatioPlusP
    visibilityControlAspectRatioPlusP.append(
      visibilityControlAspectRatioPlusButton
    );
    // visibilityControlAspectRatioSliderLabel
    visibilityControlAspectRatioSliderLabel.append(
      visibilityControlAspectRatioSliderInput
    );
    // visibilityControlAspectRatioHeader
    visibilityControlAspectRatioHeader.append(
      visibilityControlAspectRatioMinusP,
      visibilityControlAspectRatioTitle,
      visibilityControlAspectRatioPlusP
    );
    // visibilityControlAspectRatioSlider
    visibilityControlAspectRatioSlider.append(
      visibilityControlAspectRatioSliderLabel
    );
    // visibilityControlAspectRatioValue
    visibilityControlAspectRatioValue.append(
      visibilityControlAspectRatioMinValue,
      visibilityControlAspectRatioCurrentValue,
      visibilityControlAspectRatioMaxValue
    );
    // visibilityControlAspectRatioItem
    visibilityControlAspectRatioItem.append(
      visibilityControlAspectRatioHeader,
      visibilityControlAspectRatioSlider,
      visibilityControlAspectRatioValue
    );

    const visibilityControlDistanceItem = document.createElement("li");
    visibilityControlDistanceItem.classList.add("ditap-control-item");
    const visibilityControlDistanceHeader = document.createElement("div");
    visibilityControlDistanceHeader.classList.add("ditap-control-header");
    const visibilityControlDistanceMinusP = document.createElement("p");
    visibilityControlDistanceMinusP.classList.add("ditap-control-minus");
    const visibilityControlDistanceMinusButton = document.createElement(
      "button"
    );
    visibilityControlDistanceMinusButton.classList.add("ditap-control-img");
    const visibilityControlDistanceTitle = document.createElement("p");
    visibilityControlDistanceTitle.classList.add("ditap-control-title");
    visibilityControlDistanceTitle.innerText = "가시거리";
    const visibilityControlDistancePlusP = document.createElement("p");
    visibilityControlDistancePlusP.classList.add("ditap-control-plus");
    const visibilityControlDistancePlusButton = document.createElement(
      "button"
    );
    visibilityControlDistancePlusButton.classList.add("ditap-control-img");
    const visibilityControlDistanceSlider = document.createElement("div");
    visibilityControlDistanceSlider.classList.add("ditap-slider-item");
    const visibilityControlDistanceSliderLabel = document.createElement(
      "label"
    );
    const visibilityControlDistanceSliderInput = document.createElement(
      "input"
    );
    visibilityControlDistanceSliderInput.classList.add("ditap-slider");
    visibilityControlDistanceSliderInput.setAttribute("type", "range");
    visibilityControlDistanceSliderInput.setAttribute("name", "slider");
    visibilityControlDistanceSliderInput.setAttribute("min", 1);
    visibilityControlDistanceSliderInput.setAttribute("max", 1000);
    visibilityControlDistanceSliderInput.setAttribute("step", 0.1);
    const visibilityControlDistanceValue = document.createElement("div");
    visibilityControlDistanceValue.classList.add("ditap-control-footer");
    const visibilityControlDistanceMinValue = document.createElement("p");
    visibilityControlDistanceMinValue.classList.add("ditap-control-value");
    visibilityControlDistanceMinValue.innerText = 1;
    const visibilityControlDistanceCurrentValue = document.createElement("p");
    visibilityControlDistanceCurrentValue.classList.add(
      "ditap-control-value",
      "ditap-control-center-value"
    );
    visibilityControlDistanceCurrentValue.innerText = 150;
    const visibilityControlDistanceMaxValue = document.createElement("p");
    visibilityControlDistanceMaxValue.classList.add(
      "ditap-control-value",
      "ditap-control-right-value"
    );
    visibilityControlDistanceMaxValue.innerText = 1000;
    // visibilityControlDistanceMinusP
    visibilityControlDistanceMinusP.append(
      visibilityControlDistanceMinusButton
    );
    // visibilityControlDistancePlusP
    visibilityControlDistancePlusP.append(visibilityControlDistancePlusButton);
    // visibilityControlDistanceSliderLabel
    visibilityControlDistanceSliderLabel.append(
      visibilityControlDistanceSliderInput
    );
    // visibilityControlDistanceHeader
    visibilityControlDistanceHeader.append(
      visibilityControlDistanceMinusP,
      visibilityControlDistanceTitle,
      visibilityControlDistancePlusP
    );
    // visibilityControlDistanceSlider
    visibilityControlDistanceSlider.append(
      visibilityControlDistanceSliderLabel
    );
    // visibilityControlDistanceValue
    visibilityControlDistanceValue.append(
      visibilityControlDistanceMinValue,
      visibilityControlDistanceCurrentValue,
      visibilityControlDistanceMaxValue
    );
    // visibilityControlDistanceItem
    visibilityControlDistanceItem.append(
      visibilityControlDistanceHeader,
      visibilityControlDistanceSlider,
      visibilityControlDistanceValue
    );

    // visibilityControlList
    visibilityControlList.append(
      visibilityControlHeadingItem,
      visibilityControlPitchItem,
      visibilityControlFovItem,
      visibilityControlAspectRatioItem,
      visibilityControlDistanceItem
    );

    // const html = `
    //   <div class="ditap-popup hide">
    //     <div class="ditap-popup-header">
    //       <h2 class="ditap-popup-title">시점 조정</h2> -- title
    //       <button class="ditap-popup-close-btn"></button>
    //     </div>
    //     <div class="ditap-popup-content">
    //       <p class="ditap-popup-subtitle">아래의 시점을 조정해주세요</p> -- subTitle
    //       <ul class="ditap-control-list"> -- visibilityControlList
    //         <li class="ditap-control-item">  -- visibilityControlHeadingItem
    //           <div class="ditap-control-header"> -- visibilityControlHeadingHeader
    //             <p class="ditap-control-minus"> -- visibilityControlHeadingMinusP
    //               <button class="ditap-control-img"></button> -- visibilityControlHeadingMinusButton
    //             </p>
    //             <p class="ditap-control-title">수평회전</p> -- visibilityControlHeadingTitle
    //             <p class="ditap-control-plus"> -- visibilityControlHeadingPlusP
    //               <button class="ditap-control-img"></button> -- visibilityControlHeadingPlusButton
    //             </p>
    //           </div>
    //           <div class="ditap-slider-item"> -- visibilityControlHeadingSlider
    //             <label for="slider0" -- visibilityControlHeadingSliderLabel
    //               ><input -- visibilityControlHeadingSliderInput
    //                 id="slider0"
    //                 oninput="slider(this)"
    //                 class="ditap-slider"
    //                 type="range"
    //                 min="0"
    //                 max="100"
    //                 name="slider"
    //             /></label>
    //           </div>
    //           <div class="ditap-control-footer"> -- visibilityControlHeadingValue
    //             <p class="ditap-control-value">-180</p> -- visibilityControlHeadingMinValue
    //             <p class="ditap-control-value">180</p> -- visibilityControlHeadingMaxValue
    //           </div>
    //         </li>
    //         <li class="ditap-control-item">  -- visibilityControlPitchItem
    //           <div class="ditap-control-header"> -- visibilityControlPitchHeader
    //             <p class="ditap-control-minus"> -- visibilityControlPitchMinusP
    //               <button class="ditap-control-img"></button> -- visibilityControlPitchMinusButton
    //             </p>
    //             <p class="ditap-control-title">수직회전</p> -- visibilityControlPitchTitle
    //             <p class="ditap-control-plus"> -- visibilityControlPitchPlusP
    //               <button class="ditap-control-img"></button> -- visibilityControlPitchPlusButton
    //             </p>
    //           </div>
    //           <div class="ditap-slider-item"> -- visibilityControlPitchSlider
    //             <label for="slider0" -- visibilityControlPitchSliderLabel
    //               ><input -- visibilityControlPitchSliderInput
    //                 id="slider0"
    //                 oninput="slider(this)"
    //                 class="ditap-slider"
    //                 type="range"
    //                 min="0"
    //                 max="100"
    //                 name="slider"
    //             /></label>
    //           </div>
    //           <div class="ditap-control-footer"> -- visibilityControlPitchValue
    //             <p class="ditap-control-value">-180</p> -- visibilityControlPitchMinValue
    //             <p class="ditap-control-value">180</p> -- visibilityControlPitchMaxValue
    //           </div>
    //         </li>
    //         <li class="ditap-control-item">  -- visibilityControlFovItem
    //           <div class="ditap-control-header"> -- visibilityControlFovHeader
    //             <p class="ditap-control-minus"> -- visibilityControlFovMinusP
    //               <button class="ditap-control-img"></button> -- visibilityControlFovMinusButton
    //             </p>
    //             <p class="ditap-control-title">수평화각</p> -- visibilityControlFovTitle
    //             <p class="ditap-control-plus"> -- visibilityControlFovPlusP
    //               <button class="ditap-control-img"></button> -- visibilityControlFovPlusButton
    //             </p>
    //           </div>
    //           <div class="ditap-slider-item"> -- visibilityControlFovSlider
    //             <label for="slider0" -- visibilityControlFovSliderLabel
    //               ><input -- visibilityControlFovSliderInput
    //                 id="slider0"
    //                 oninput="slider(this)"
    //                 class="ditap-slider"
    //                 type="range"
    //                 min="0"
    //                 max="100"
    //                 name="slider"
    //             /></label>
    //           </div>
    //           <div class="ditap-control-footer"> -- visibilityControlFovValue
    //             <p class="ditap-control-value">-180</p> -- visibilityControlFovMinValue
    //             <p class="ditap-control-value">180</p> -- visibilityControlFovMaxValue
    //           </div>
    //         </li>
    //         <li class="ditap-control-item">  -- visibilityControlAspectRatioItem
    //           <div class="ditap-control-header"> -- visibilityControlAspectRatioHeader
    //             <p class="ditap-control-minus"> -- visibilityControlAspectRatioMinusP
    //               <button class="ditap-control-img"></button> -- visibilityControlAspectRatioMinusButton
    //             </p>
    //             <p class="ditap-control-title">수직화각</p> -- visibilityControlAspectRatioTitle
    //             <p class="ditap-control-plus"> -- visibilityControlAspectRatioPlusP
    //               <button class="ditap-control-img"></button> -- visibilityControlAspectRatioPlusButton
    //             </p>
    //           </div>
    //           <div class="ditap-slider-item"> -- visibilityControlAspectRatioSlider
    //             <label for="slider0" -- visibilityControlAspectRatioSliderLabel
    //               ><input -- visibilityControlAspectRatioSliderInput
    //                 id="slider0"
    //                 oninput="slider(this)"
    //                 class="ditap-slider"
    //                 type="range"
    //                 min="0"
    //                 max="100"
    //                 name="slider"
    //             /></label>
    //           </div>
    //           <div class="ditap-control-footer"> -- visibilityControlAspectRatioValue
    //             <p class="ditap-control-value">-180</p> -- visibilityControlAspectRatioMinValue
    //             <p class="ditap-control-value">180</p> -- visibilityControlAspectRatioMaxValue
    //           </div>
    //         </li>
    //         <li class="ditap-control-item">  -- visibilityControlDistanceItem
    //           <div class="ditap-control-header"> -- visibilityControlDistanceHeader
    //             <p class="ditap-control-minus"> -- visibilityControlDistanceMinusP
    //               <button class="ditap-control-img"></button> -- visibilityControlDistanceMinusButton
    //             </p>
    //             <p class="ditap-control-title">가시거리</p> -- visibilityControlDistanceTitle
    //             <p class="ditap-control-plus"> -- visibilityControlDistancePlusP
    //               <button class="ditap-control-img"></button> -- visibilityControlDistancePlusButton
    //             </p>
    //           </div>
    //           <div class="ditap-slider-item"> -- visibilityControlDistanceSlider
    //             <label for="slider0" -- visibilityControlDistanceSliderLabel
    //               ><input -- visibilityControlDistanceSliderInput
    //                 id="slider0"
    //                 oninput="slider(this)"
    //                 class="ditap-slider"
    //                 type="range"
    //                 min="0"
    //                 max="100"
    //                 name="slider"
    //             /></label>
    //           </div>
    //           <div class="ditap-control-footer"> -- visibilityControlDistanceValue
    //             <p class="ditap-control-value">-180</p> -- visibilityControlDistanceMinValue
    //             <p class="ditap-control-value">180</p> -- visibilityControlDistanceMaxValue
    //           </div>
    //         </li>
    //       </ul>
    //     </div>
    //   </div>
    // `

    return {
      header: {
        title: title,
      },
      content: {
        subTitle: subTitle,

        visibilityControlList: visibilityControlList,
        visibilityControlHeadingMinusP: visibilityControlHeadingMinusP,
        visibilityControlHeadingPlusP: visibilityControlHeadingPlusP,
        visibilityControlHeadingSliderInput: visibilityControlHeadingSliderInput,
        visibilityControlHeadingMinValue: visibilityControlHeadingMinValue,
        visibilityControlHeadingCurrentValue: visibilityControlHeadingCurrentValue,
        visibilityControlHeadingMaxValue: visibilityControlHeadingMaxValue,

        visibilityControlPitchMinusP: visibilityControlPitchMinusP,
        visibilityControlPitchPlusP: visibilityControlPitchPlusP,
        visibilityControlPitchSliderInput: visibilityControlPitchSliderInput,
        visibilityControlPitchMinValue: visibilityControlPitchMinValue,
        visibilityControlPitchCurrentValue: visibilityControlPitchCurrentValue,
        visibilityControlPitchMaxValue: visibilityControlPitchMaxValue,

        visibilityControlFovMinusP: visibilityControlFovMinusP,
        visibilityControlFovPlusP: visibilityControlFovPlusP,
        visibilityControlFovSliderInput: visibilityControlFovSliderInput,
        visibilityControlFovMinValue: visibilityControlFovMinValue,
        visibilityControlFovCurrentValue: visibilityControlFovCurrentValue,
        visibilityControlFovMaxValue: visibilityControlFovMaxValue,

        visibilityControlAspectRatioMinusP: visibilityControlAspectRatioMinusP,
        visibilityControlAspectRatioPlusP: visibilityControlAspectRatioPlusP,
        visibilityControlAspectRatioSliderInput: visibilityControlAspectRatioSliderInput,
        visibilityControlAspectRatioMinValue: visibilityControlAspectRatioMinValue,
        visibilityControlAspectRatioCurrentValue: visibilityControlAspectRatioCurrentValue,
        visibilityControlAspectRatioMaxValue: visibilityControlAspectRatioMaxValue,

        visibilityControlDistanceMinusP: visibilityControlDistanceMinusP,
        visibilityControlDistancePlusP: visibilityControlDistancePlusP,
        visibilityControlDistanceSliderInput: visibilityControlDistanceSliderInput,
        visibilityControlDistanceMinValue: visibilityControlDistanceMinValue,
        visibilityControlDistanceCurrentValue: visibilityControlDistanceCurrentValue,
        visibilityControlDistanceMaxValue: visibilityControlDistanceMaxValue,
      },
    };
  }
}

export default PopupAnalysisVisibility;
